import { Component, ElementRef, EventEmitter, HostBinding, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { BorderConfig, BorderStyle, ClickspotType, HotspotLayoutEnum } from '../../../../common/models';
import { ColorHelperService } from '../../../../common/services';


@Component({
    selector: 'lib-hotspot-marker',
    templateUrl: './hotspot-marker.component.html',
    styleUrls: ['./hotspot-marker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HotspotMarkerComponent  {
  @Input() @HostBinding('class') public type: ClickspotType = ClickspotType.PIN;

  @Input() public icon!: string;
  @Input() public index!: number;

  @Input() public set x(value: number) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-x', value ? `${value}%` : null);
  }

  @Input() public set y(value: number) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-y', value ? `${value}%` : null);
  }

  @Input() public set width(value: number) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-width', value ? `${value}%` : null);
  }

  @Input() public set height(value: number) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-height', value ? `${value}%` : null);
  }

  @Input() public set fillColor(value: string) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-fill-color', value ?? '');
  }

  @Input() public set hotspotBorderStyle(value: BorderStyle | undefined) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-border-style', value ?? '');
  }

  @Input() public set borderColor(value: string) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-border-color', value ?? '');
    this.element.nativeElement.style.setProperty(
      '--lms-hotspot-text-color',
      value ? this.colorHelperService.getContrastingTextColor(value) : ''
    );
  }

  @Input() public iconColor!: string;
  @Input() public layout!: HotspotLayoutEnum;
  @Input() public spotsAnimation?: string;
  @Input() public animate = false;
  @Input() public active = false;
  @Input() public selected = false;
  @Input() public visited = false;
  @Input() public resizable = false;
  @Input() public showEditButton = false;
  @Input() public border?: BorderConfig;
  @Input() public activeStyle?: {
    color?: string;
    iconColor?: string;
    backgroundColor?: string;
    backgroundBlur?: string;
    border?: BorderConfig;
  };

  @Input() public set color(value: string) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-background', value ?? '');
  
    this.whiteBackground = value ? !this.colorHelperService.isColorDark(value) : false;
  }

  @Input() public set blur(value: string) {
    this.element.nativeElement.style.setProperty('--lms-hotspot-marker-blur', value);
  }

  public whiteBackground = false;

  public get cssClasses(): Record<string, boolean> {
    return {
      active: this.active,
      selected: this.selected,
      'white-background': this.whiteBackground,
      ...(this.animate && this.spotsAnimation
        ? { animate__animated: true, [this.spotsAnimation]: true }
        : {})
    };
  }

  public get animationDelayMs(): number {
    return (this.index + 1) * 200;
  }

  public readonly layouts: typeof HotspotLayoutEnum = HotspotLayoutEnum;
  public readonly types: typeof ClickspotType = ClickspotType;

  @Output() public sizeChanged = new EventEmitter<{ width: number, height: number, index: number}>();
  @Output() public edit: EventEmitter<void> = new EventEmitter();
  @Output() public markerClick: EventEmitter<void> = new EventEmitter();

  public onSizeChange(size: { width: number, height: number }): void {
    this.sizeChanged.emit({ ...size, index: this.index });
  }

  constructor(
    public readonly element: ElementRef,
    private readonly colorHelperService: ColorHelperService) { }
}
